import { displayText } from '../../../../terminology/store';
import { useTerminology } from '../../../../terminology/TerminologyContext';
import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import axios from 'axios';
import ThemeContext from '../../../ThemeContext';
import * as Yup from 'yup';
import { FULL_ROUTES, URL_SEGMENTS, SECTION_LABELS } from '../../../../config/Routesconfig';

const adminSchema = Yup.object({
  admin_name: Yup.string()
    .required('Please fill this field.')
    .min(3, 'Admin Name must be at least 3 characters.')
    .matches(/^[a-zA-Z][a-zA-Z\s]*[a-zA-Z]$/, 'Admin Name must not start or end with a number or special character.'),
  email: Yup.string()
    .required('Please fill this field.')
    .min(8, 'Email must be at least 8 characters long.')
    .matches(/[^.!@}]/, 'Email must not start with a dot (.), exclamation mark (!), at symbol (@), or curly brace (}).')
    .matches(/^[^@]*@[^@]*$/, 'Email must contain exactly one @ symbol.')
    .matches(/@[^.]*\.[^.].*/, 'Email must include a domain after @ (e.g., example.com).')
    .matches(/@.*\.[a-zA-Z]{2,}$/, 'Email must end with a valid domain extension (e.g., .com, .org) containing only letters.')
    .matches(/@.*\.[^.]*/, 'Email must include a domain after . (e.g., example.com).')
    .matches(/@.*[a-zA-Z]{2,}\./, 'Domain name must have at least 2 letters before the dot.')
    .matches(/@.*\.[a-zA-Z]{2,}$/, 'Domain extension must have at least 2 letters after the dot.')
    .matches(/^[^.].*[^.]$/, 'Email cannot start or end with a dot.')
    .matches(/^(?!.*\.\.).*$/, 'Email cannot contain consecutive dots (e.g., ..).')
    .email('Invalid email address.'),
  username: Yup.string()
    .required('Please fill this field.')
    .min(3, 'Username must be at least 3 characters.'),
  password: Yup.string()
    .required('Please fill this field.')
    .min(8, 'Password must be at least 8 characters.')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter.')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter.')
    .matches(/[0-9]/, 'Password must contain at least one number.')
    .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character.'),
  password_confirmation: Yup.string()
    .required('Please fill this field.')
    .oneOf([Yup.ref('password'), null], 'Passwords do not match.'),
});

// Derived paths from RoutesConfig
const adminsListPath = `/${URL_SEGMENTS.superadmin}/${URL_SEGMENTS.admins}`;
const adminsLabel    = SECTION_LABELS.admins; // "Admins"

const AddAdmin = () => {
  useTerminology();
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();

  // Typography from theme
  const fontFamily =
    theme.typography?.fontFamily ||
    '"Roboto", "Helvetica Neue", Arial, sans-serif';
  const bodyFontSize = theme.typography?.bodyFontSize || 14;
  const titleFontSize = theme.typography?.titleFontSize || 24;

  const [formData, setFormData] = useState({
    admin_name: '',
    email: '',
    username: '',
    password: '',
    password_confirmation: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch CSRF token
  useEffect(() => {
    const fetchCsrf = async () => {
      try {
        await axios.get(`${process.env.REACT_APP_BACKEND_URL}/sanctum/csrf-cookie`, {
          withCredentials: true,
        });
      } catch (error) {
        console.error('Error fetching CSRF token:', error);
        toast.error('Failed to initialize session.');
      }
    };
    fetchCsrf();
  }, []);

  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }

    try {
      await adminSchema.validateAt(name, { ...formData, [name]: value });
      setErrors((prev) => ({ ...prev, [name]: '' }));
    } catch (error) {
      setErrors((prev) => ({ ...prev, [name]: error.message }));
    }
  };

  const validateForm = async () => {
    try {
      await adminSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (validationErrors) {
      const newErrors = {};
      validationErrors.inner.forEach((err) => {
        newErrors[err.path] = err.message;
      });
      setErrors(newErrors);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = await validateForm();
    if (!isValid) {
      toast.error('Please correct the errors and try again.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/webmanager/admins`,
        formData,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }
      );

      toast.success(response.data.message || 'Admin created successfully!');
      // Navigate to admins list — path from RoutesConfig
      setTimeout(() => navigate(adminsListPath), 2000);
    } catch (error) {
      console.error('Error creating admin:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create admin.';
      const backendErrors = error.response?.data?.errors || {};
      setErrors((prev) => ({ ...prev, ...backendErrors }));
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cancel — go back to admins list path from RoutesConfig
  const handleCancel = () => {
    navigate(adminsListPath);
  };

  return (
    <div style={{ fontFamily, fontSize: `${bodyFontSize}px` }}>
      {/* BREADCRUMB — paths and labels from RoutesConfig */}
      <div
        style={{
          marginTop: '10px',
          marginBottom: '0.5rem',
          paddingLeft: '1rem',
          fontSize: `${Math.max(11, bodyFontSize - 1)}px`,
          fontFamily,
        }}
      >
        <span style={{ color: theme.colors.primary }}>
          <Link
            to={adminsListPath}
            style={{
              color: theme.colors.primary,
              textDecoration: 'underline',
              fontFamily,
            }}
          >
            {adminsLabel} Management
          </Link>
        </span>
        <span style={{ margin: '0 0.5rem', color: theme.colors.textPrimary }}>&gt;</span>
        <span style={{ color: theme.colors.textPrimary }}>Add New {adminsLabel}</span>
      </div>

      {/* WHITE CARD CONTAINER */}
      <div
        style={{
          backgroundColor: '#ffffff',
          padding: '1rem',
          borderRadius: '10px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          fontFamily,
          marginTop: '0',
          marginBottom: '20px',
        }}
      >
        <ToastContainer position="top-right" autoClose={3000} />

        {/* Loading Overlay */}
        {isSubmitting && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 3000,
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                border: `4px solid ${theme.colors.primary}`,
                borderTop: '4px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
          </div>
        )}

        {/* Title */}
        <h4
          style={{
            fontSize: `${titleFontSize}px`,
            fontWeight: 'bold',
            color: theme.colors.textPrimary,
            marginBottom: '1rem',
            fontFamily,
          }}
        >
          Add New {adminsLabel}
        </h4>

        {/* Form Content */}
        <div style={{ padding: '1rem 0' }}>
          <div className="row g-3">
            <div className="col-md-4">
              <label style={{ fontWeight: '500', color: theme.colors.textPrimary, fontFamily }}>
                Admin Name*
              </label>
              <input
                type="text"
                name="admin_name"
                value={formData.admin_name}
                onChange={handleInputChange}
                className={`form-control mt-1 ${errors.admin_name ? 'is-invalid' : ''}`}
                style={{
                  backgroundColor: '#fff',
                  border: `1px solid ${errors.admin_name ? theme.colors.danger : '#dee2e6'}`,
                  fontSize: `${bodyFontSize}px`,
                  fontFamily,
                }}
              />
              {errors.admin_name && (
                <div style={{ color: theme.colors.danger, fontSize: `${bodyFontSize - 2}px`, marginTop: '0.25rem' }}>
                  {errors.admin_name}
                </div>
              )}
            </div>
          </div>

          <h5 style={{ margin: '1.5rem 0 1rem', color: theme.colors.textPrimary, fontFamily }}>
            Login Credentials
          </h5>

          <div className="row g-3">
            <div className="col-md-4">
              <label style={{ fontWeight: '500', color: theme.colors.textPrimary, fontFamily }}>
                Email*
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`form-control mt-1 ${errors.email ? 'is-invalid' : ''}`}
                style={{
                  backgroundColor: '#fff',
                  border: `1px solid ${errors.email ? theme.colors.danger : '#dee2e6'}`,
                  fontSize: `${bodyFontSize}px`,
                  fontFamily,
                }}
              />
              {errors.email && (
                <div style={{ color: theme.colors.danger, fontSize: `${bodyFontSize - 2}px`, marginTop: '0.25rem' }}>
                  {errors.email}
                </div>
              )}
            </div>

            <div className="col-md-4">
              <label style={{ fontWeight: '500', color: theme.colors.textPrimary, fontFamily }}>
                Username*
              </label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                className={`form-control mt-1 ${errors.username ? 'is-invalid' : ''}`}
                style={{
                  backgroundColor: '#fff',
                  border: `1px solid ${errors.username ? theme.colors.danger : '#dee2e6'}`,
                  fontSize: `${bodyFontSize}px`,
                  fontFamily,
                }}
              />
              {errors.username && (
                <div style={{ color: theme.colors.danger, fontSize: `${bodyFontSize - 2}px`, marginTop: '0.25rem' }}>
                  {errors.username}
                </div>
              )}
            </div>
          </div>

          <div className="row g-3 mt-3">
            <div className="col-md-4">
              <label style={{ fontWeight: '500', color: theme.colors.textPrimary, fontFamily }}>
                Password*
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                className={`form-control mt-1 ${errors.password ? 'is-invalid' : ''}`}
                style={{
                  backgroundColor: '#fff',
                  border: `1px solid ${errors.password ? theme.colors.danger : '#dee2e6'}`,
                  fontSize: `${bodyFontSize}px`,
                  fontFamily,
                }}
              />
              {errors.password && (
                <div style={{ color: theme.colors.danger, fontSize: `${bodyFontSize - 2}px`, marginTop: '0.25rem' }}>
                  {errors.password}
                </div>
              )}
            </div>

            <div className="col-md-4">
              <label style={{ fontWeight: '500', color: theme.colors.textPrimary, fontFamily }}>
                Retype Password*
              </label>
              <input
                type="password"
                name="password_confirmation"
                value={formData.password_confirmation}
                onChange={handleInputChange}
                className={`form-control mt-1 ${errors.password_confirmation ? 'is-invalid' : ''}`}
                style={{
                  backgroundColor: '#fff',
                  border: `1px solid ${errors.password_confirmation ? theme.colors.danger : '#dee2e6'}`,
                  fontSize: `${bodyFontSize}px`,
                  fontFamily,
                }}
              />
              {errors.password_confirmation && (
                <div style={{ color: theme.colors.danger, fontSize: `${bodyFontSize - 2}px`, marginTop: '0.25rem' }}>
                  {errors.password_confirmation}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '2rem' }}>
            <button
              onClick={handleCancel}
              disabled={isSubmitting}
              style={{
                backgroundColor: theme.colors.secondary,
                color: theme.colors.textWhite,
                padding: '8px 16px',
                borderRadius: '6px',
                border: 'none',
                fontSize: `${bodyFontSize}px`,
                fontFamily,
                minWidth: '100px',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              style={{
                backgroundColor: theme.colors.primary,
                color: theme.colors.textWhite,
                padding: '8px 16px',
                borderRadius: '6px',
                border: 'none',
                fontSize: `${bodyFontSize}px`,
                fontFamily,
                minWidth: '100px',
                opacity: isSubmitting ? 0.7 : 1,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
              }}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Spinner Animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default AddAdmin;